import React, { useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Checkbox, Pagination, Popover, Select, Progress, Result, Upload } from 'antd';
import { ArrowLeftOutlined, SearchOutlined, UploadOutlined, DownloadOutlined, LoadingOutlined, CloseCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { SbButton, SbNotification, SbFilePill, SbUpload, SbTable, SbTabs, SbTooltip, SbSpin, SbSelect, SbModal, SbTag, FilterOutlined } from 'uiCore';
import { PageTableWrapperStyled } from 'styles/common.styles';
import { AuthContext } from 'State/AuthContext';
import { validateExcelFile } from 'Utils/fileValidation';
import { showSbConfirm } from 'Utils/sbModal';
import useHrisUploadSSE from 'Hooks/useHrisUploadSSE';
import useHrisOnboardSSE from 'Hooks/useHrisOnboardSSE';
import useHrisUploadStore from 'Store/useHrisUploadStore';
import useNotificationStoreV2 from 'Store/useNotificationStoreV2';
import HistoricalEmployeeDataUpload from './HistoricalEmployeeDataUpload';
import {
  uploadOrgHierarchyEmployees,
  processOrgHierarchyMapping,
  confirmOrgHierarchyMapping,
  onboardMappedOrgHierarchyEmployees,
  getOrgHierarchyEmployees,
  downloadOrgHierarchyTemplate,
} from 'Api/apiService';
import {
  PageContainer,
  HeaderSection,
  HeaderTopRow,
  HeaderLeft,
  BackButton,
  TitleGroup,
  PageTitle,
  PageSubtitle,
  TabsRow,
  ContentSection,
  BulkToolbarRow,
  MappingActionRow,
  MappingActionsGroup,
  MappingConfirmedText,
  PremiumButtonWrapper,
  OnboardResultBanner,
  OnboardResultIcon,
  OnboardResultBody,
  OnboardResultActions,
  SubtitleArrow,
  MappingTabBar,
  MappingTabBtn,
  ReqIdLink,
  ReqMetaWrapper,
  ReqMetaItem,
  ReqMetaLabel,
  ReqMetaValue,
  ReqPickerList,
  ReqPickerCard,
  ReqPickerCardTitle,
  ReqPickerCardMeta,
  ReqChipRow,
  ReqChip,
  ReqMoreChip,
  NoMatchIcon,
  HrisUploadRow,
  ColMapSection,
  ColMapCard,
  ColMapCardFixed,
  ColMapCardBody,
  ColMapFooter,
  OnboardingKeyHint,
  OnboardingKeyField,
  ColMapTableHeader,
  ColMapThField,
  ColMapThArrow,
  ColMapThFile,
  ColMapThStatus,
  ColMapGroupLabel,
  ColMapFieldRow,
  ColMapFieldCell,
  ColMapArrowCol,
  ColMapFieldLabel,
  ColMapFieldArrow,
  ColMapSelectCell,
  ColMapStatusCell,
  ColMapStatusPendingIcon,
  ColMapStatusCheckIcon,
  ColMapReqStar,
  ColMapGroupDivider,
  ColMapFullNameCheckRow,
  ColHeaderWrapper,
  ColHeaderTitle,
  ColHeaderFilterSection,
  ColHeaderFilterIcon,
  ColHeaderSelectWrap,
  EmptyStateWrapper,
  UploadCTASection,
  OnboardJourneyRow,
  OnboardJourneyBar,
  OnboardJourneyStep,
  OnboardJourneyBadge,
  OnboardJourneyLabel,
  OnboardJourneyDivider,
  HrisDraggerWrap,
  EmpSearchWrapperStyled,
  EmpViewTableWrap,
} from './OrganizationHierarchy.styles';

// Date of Joining / Role Start Date are stored as BQ DATE values (YYYY-MM-DD) —
// render blank/unparseable values as '-' the same way every other optional
// column here does, rather than showing "Invalid Date".
const formatOnboardingDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const SHEET_NAME   = 'employee data';
const MAX_ROWS     = 5000;
const MANDATORY_COLS = [
  'emp id', 'first name', 'last name', 'job code', 'job title',
  'manager emp id', 'business unit', 'legal entity', 'function',
  'sub-function', 'hrbp emp id', 'hrbp first name', 'hrbp last name', 'level / grade',
];

// Column display config — widths sized to actual data, not field name length
const COL_CONFIG = {
  emp_id:          { title: 'Emp ID',          width: 70  },
  first_name:      { title: 'First Name',       width: 120 },
  last_name:       { title: 'Last Name',        width: 100 },
  job_code:        { title: 'Job Code',         width: 110 },
  job_title:       { title: 'Job Title',        width: 180 },
  manager_emp_id:  { title: 'Manager Emp ID',   width: 120 },
  business_unit:   { title: 'Business Unit',    width: 120 },
  legal_entity:    { title: 'Legal Entity',     width: 115 },
  function:        { title: 'Function',         width: 110 },
  sub_function:    { title: 'Sub-Function',     width: 115 },
  level:           { title: 'Level / Grade',    width: 80  },
  hrbp_emp_id:     { title: 'HRBP Emp ID',      width: 80  },
  hrbp_first_name: { title: 'HRBP First Name',  width: 100 },
  hrbp_last_name:  { title: 'HRBP Last Name',   width: 100 },
  employee_email_id:  { title: 'Employee Email',   width: 200 },
  date_of_joining: { title: 'Date of Joining',  width: 130 },
  role_start_date: { title: 'Role Start Date',  width: 130 },
  role_id:         { title: 'Role ID',          width: 120 },
};

const MANDATORY_DISPLAY = ['emp_id', 'first_name', 'last_name', 'job_code', 'job_title', 'manager_emp_id', 'hrbp_emp_id', 'hrbp_first_name', 'hrbp_last_name'];
const OPTIONAL_DISPLAY  = ['business_unit', 'legal_entity', 'function', 'sub_function', 'level', 'employee_email_id', 'role_id', 'date_of_joining', 'role_start_date'];

// The 5 fields that unlock "Proceed to Map Employees"
const KEY_FIELDS = ['emp_id', 'job_code', 'job_title', 'manager_emp_id', 'hrbp_emp_id'];

const ONBOARD_JOURNEY = [
  'Upload file',
  'Map columns with SkillsBridge headers',
  'Review mapping & onboard employees',
];

// Config for name field pairs that support Full Name mode
const FULL_NAME_HINT = 'Select if your file has a full name column for this field instead of separate first and last name columns';

const NAME_GROUP_CONFIG = {
  emp:  { firstKey: 'first_name',         lastKey: 'last_name',         fullKey: 'full_name',         fullLabel: 'Employee Full Name', hint: FULL_NAME_HINT },
  mgr:  { firstKey: 'manager_first_name', lastKey: 'manager_last_name', fullKey: 'manager_full_name', fullLabel: 'Manager Full Name',  hint: FULL_NAME_HINT },
  hrbp: { firstKey: 'hrbp_first_name',    lastKey: 'hrbp_last_name',    fullKey: 'hrbp_full_name',    fullLabel: 'HRBP Full Name',     hint: FULL_NAME_HINT },
};

const FIELD_GROUPS = [
  {
    label: 'Employee Details',
    fields: [
      { key: 'emp_id',         label: 'Employee ID' },
      { key: 'first_name',     label: 'First Name',      nameGroup: 'emp' },
      { key: 'last_name',      label: 'Last Name',       nameGroup: 'emp' },
      { key: 'employee_email_id', label: 'Employee Email',  validate: 'email' },
    ],
  },
  {
    label: 'Job Details',
    fields: [
      { key: 'job_code',      label: 'Job Code' },
      { key: 'job_title',     label: 'Job Title' },
      { key: 'role_id',       label: 'Role ID' },
      { key: 'function',      label: 'Function' },
      { key: 'sub_function',  label: 'Sub-Function' },
      { key: 'business_unit', label: 'Business Unit' },
      { key: 'legal_entity',  label: 'Legal Entity' },
      { key: 'level',         label: 'Level / Grade' },
      { key: 'date_of_joining', label: 'Date of Joining' },
      { key: 'role_start_date', label: 'Role Start Date' },
    ],
  },
  {
    label: 'Manager Details',
    fields: [
      { key: 'manager_emp_id',     label: 'Manager Employee ID' },
      { key: 'manager_first_name', label: 'Manager First Name', nameGroup: 'mgr' },
      { key: 'manager_last_name',  label: 'Manager Last Name',  nameGroup: 'mgr' },
    ],
  },
  {
    label: 'Human Resources Business Partner (HRBP) Details',
    fields: [
      { key: 'hrbp_emp_id',    label: 'HRBP Employee ID' },
      { key: 'hrbp_first_name', label: 'HRBP First Name', nameGroup: 'hrbp' },
      { key: 'hrbp_last_name',  label: 'HRBP Last Name',  nameGroup: 'hrbp' },
    ],
  },
];



const ReqPopoverContent = ({ meta, jobId }) => {
  if (!meta) return null;
  const rows = [
    ...(jobId ? [['Job ID', jobId]] : []),
    ['Job Title',     meta.alt_role_title || meta.job_title],
    ['Function',      meta.function],
    ['Sub-Function',  meta.sub_function],
    ['Business Unit', meta.business_unit],
    ['Legal Entity',  meta.legal_entity],
    ['Level',         meta.level],
  ].filter(([, v]) => v);
  return (
    <ReqMetaWrapper>
      {rows.map(([label, value]) => (
        <ReqMetaItem key={label}>
          <ReqMetaLabel>{label}</ReqMetaLabel>
          <ReqMetaValue>{value}</ReqMetaValue>
        </ReqMetaItem>
      ))}
    </ReqMetaWrapper>
  );
};

const INLINE_CHIP_LIMIT = 2;

const ReqPickerCell = ({ record, resolved, onResolve, locked }) => {
  const [open, setOpen] = useState(false);
  const options = record.possible_requisitions_json ?? [];
  const selectedOption = options.find((r) => r.sb_requisition_number === resolved);

  // Once selected — collapse to just the selected chip. Once mapping is confirmed, the
  // choice is locked (no discard ×) so a stray click can't silently undo a confirmed mapping.
  if (selectedOption) {
    return (
      <ReqChipRow>
        <Popover
          content={<ReqPopoverContent meta={selectedOption} jobId={selectedOption.sb_requisition_number} />}
          title="Requisition Details"
          trigger="hover"
          placement="top"
          mouseEnterDelay={0.3}
        >
          <ReqChip $selected onClick={locked ? undefined : () => onResolve(record.mapping_id, null)} style={locked ? { cursor: 'default' } : undefined}>
            <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, width: '100%' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedOption.alt_role_title || selectedOption.job_title}</span>
              {!locked && <span style={{ flexShrink: 0 }}>×</span>}
            </span>
            <span style={{ fontSize: 10, opacity: 0.7, fontWeight: 400, whiteSpace: 'nowrap' }}>{selectedOption.sb_requisition_number}</span>
          </ReqChip>
        </Popover>
      </ReqChipRow>
    );
  }

  // Mapping already confirmed but this row was never resolved — show as read-only, not pickable
  if (locked) {
    return <span style={{ fontSize: 12, color: '#8c8c8c' }}>Not resolved</span>;
  }

  // No selection — show first N inline, rest behind + more popover
  const visible = options.slice(0, INLINE_CHIP_LIMIT);
  const overflow = options.slice(INLINE_CHIP_LIMIT);

  const moreContent = (
    <ReqPickerList>
      {overflow.map((r) => (
        <ReqPickerCard
          key={r.sb_requisition_number}
          onClick={() => { onResolve(record.mapping_id, r.sb_requisition_number); setOpen(false); }}
        >
          <ReqPickerCardTitle>{r.sb_requisition_number} · {r.alt_role_title || r.job_title}</ReqPickerCardTitle>
          <ReqPickerCardMeta>
            {[r.function, r.sub_function].filter(Boolean).join(' › ')}
            {r.business_unit ? ` · ${r.business_unit}` : ''}
            {r.level ? ` · ${r.level}` : ''}
          </ReqPickerCardMeta>
        </ReqPickerCard>
      ))}
    </ReqPickerList>
  );

  return (
    <ReqChipRow>
      {visible.map((r) => (
        <Popover
          key={r.sb_requisition_number}
          content={<ReqPopoverContent meta={r} jobId={r.sb_requisition_number} />}
          title="Requisition Details"
          trigger="hover"
          placement="top"
          mouseEnterDelay={0.3}
        >
          <ReqChip onClick={() => onResolve(record.mapping_id, r.sb_requisition_number)}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{r.alt_role_title || r.job_title}</span>
            <span style={{ fontSize: 10, opacity: 0.6, fontWeight: 400, whiteSpace: 'nowrap' }}>{r.sb_requisition_number}</span>
          </ReqChip>
        </Popover>
      ))}
      {overflow.length > 0 && (
        <Popover
          content={moreContent}
          title="More options"
          trigger="click"
          open={open}
          onOpenChange={setOpen}
          placement="leftTop"
        >
          <ReqMoreChip>+{overflow.length} more</ReqMoreChip>
        </Popover>
      )}
    </ReqChipRow>
  );
};

const NEEDS_ACTION_HIDDEN_COLS = new Set(['manager_emp_id', 'hrbp_emp_id', 'hrbp_first_name', 'hrbp_last_name']);
const WRAP_COLS = new Set(['emp_id', 'first_name', 'last_name', 'job_code', 'job_title', 'business_unit', 'legal_entity', 'function', 'sub_function']);

const FILTER_OPTIONS = [
  { label: 'All',            value: 'all'      },
  { label: 'Multiple Match', value: 'multiple' },
  { label: 'No Match',       value: 'none'     },
];

const EMP_TABS = [
  { key: 'onboard', label: 'Onboard Employees' },
  { key: 'view',    label: 'View Employees'    },
  { key: 'history', label: 'Historical Employee Data' },
];

// View Employees loads the full dataset in one call so the table's filters and search work across every record, not just the current page.
const EMP_VIEW_FETCH_ALL_LIMIT = 100000;

const buildColumns = (data, type, resolvedMap = {}, onResolve = () => {}, activeFilter = 'all', onFilterChange = () => {}, locked = false) => {
  if (!data.length) return [];
  const presentOptional = OPTIONAL_DISPLAY.filter((k) => data.some((row) => row[k]));
  let allKeys = [...MANDATORY_DISPLAY, ...presentOptional].filter((k) => !NEEDS_ACTION_HIDDEN_COLS.has(k));
  const base = allKeys.map((key) => ({
    ...COL_CONFIG[key],
    dataIndex: key,
    key,
    ellipsis: !WRAP_COLS.has(key),
  }));

  if (type === 'mapped') {
    base.push({
      title: 'Requisition',
      dataIndex: 'matched_sb_requisition_number',
      key: 'matched_sb_requisition_number',
      width: 145,
      render: (val, record) => {
        const meta = record.possible_requisitions_json?.find((r) => r.sb_requisition_number === val);
        const displayTitle = meta?.alt_role_title || meta?.job_title || val;
        return (
          <Popover
            content={<ReqPopoverContent meta={meta} jobId={val} />}
            title="Requisition Details"
            trigger="hover"
            placement="leftTop"
            mouseEnterDelay={0.3}
          >
            <ReqIdLink>{displayTitle}</ReqIdLink>
          </Popover>
        );
      },
    });
  }

  if (type === 'needs_action') {
    base.push({
      title: (
        <ColHeaderWrapper>
          <ColHeaderTitle>
            Select Job Id
            <SbTooltip title="Job Code not found — create new jobcode & necessary roles in SkillsBridge" placement="top">
              <span><NoMatchIcon $size="13px" /></span>
            </SbTooltip>
          </ColHeaderTitle>
          <ColHeaderFilterSection>
            <ColHeaderFilterIcon $active={activeFilter !== 'all'} />
            <ColHeaderSelectWrap>
              <Select
                size="small"
                value={activeFilter}
                options={FILTER_OPTIONS}
                onChange={onFilterChange}
                onClick={(e) => e.stopPropagation()}
              />
            </ColHeaderSelectWrap>
          </ColHeaderFilterSection>
        </ColHeaderWrapper>
      ),
      key: 'resolve',
      width: 340,
      // minWidth+maxWidth on the cells (not colgroup) locks this column to exactly 340px
      // in auto table-layout, letting all other columns share remaining space proportionally
      onHeaderCell: () => ({ style: { paddingLeft: 2, minWidth: 340, maxWidth: 340 } }),
      onCell: () => ({ style: { paddingLeft: 2, minWidth: 340, maxWidth: 340 } }),
      render: (_, record) => {
        const resolved = resolvedMap[record.mapping_id];
        if (!record.possible_requisitions_json?.length) {
          return (
            <div style={{ width: 180, display: 'flex', justifyContent: 'center' }}>
              <SbTooltip title="Job Code not found — create new jobcode & necessary roles in SkillsBridge" placement="top">
                <NoMatchIcon />
              </SbTooltip>
            </div>
          );
        }
        return <ReqPickerCell record={record} resolved={resolved} onResolve={onResolve} locked={locked} />;
      },
    });
  }

  return base;
};

const parseAndValidate = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const matchedSheet = workbook.SheetNames.find((n) => n.trim().toLowerCase() === SHEET_NAME);
        if (!matchedSheet)
          return reject('Sheet named "Employee Data" not found. Please use the downloaded template.');
        const rawRows = XLSX.utils.sheet_to_json(workbook.Sheets[matchedSheet], { defval: '' });
        if (rawRows.length === 0)
          return reject('The sheet has no data rows. Please add at least one employee.');
        if (rawRows.length > MAX_ROWS)
          return reject(`File exceeds the ${MAX_ROWS.toLocaleString()} row limit (found ${rawRows.length.toLocaleString()}).`);
        const fileHeaders = Object.keys(rawRows[0]).map((h) => h.trim().toLowerCase().replace(/\s+/g, ' '));
        const missing = MANDATORY_COLS.filter((c) => !fileHeaders.includes(c));
        if (missing.length > 0)
          return reject(`Missing column(s): ${missing.map((c) => `"${c}"`).join(', ')}.`);
        resolve();
      } catch {
        reject('Failed to read the file. Please ensure it is a valid .xls or .xlsx file.');
      }
    };
    reader.onerror = () => reject('Failed to read the file.');
    reader.readAsArrayBuffer(file);
  });

// Sheets to skip when the workbook is the downloaded template (Instructions + Employee Data)
const NON_DATA_SHEET_NAMES = ['instructions', 'instruction'];

// Picks the sheet to validate/read: prefer an exact "Employee Data" sheet (the template),
// otherwise fall back to the first sheet unless it's the Instructions sheet.
const selectEmployeeDataSheet = (sheetNames) => {
  const employeeDataSheet = sheetNames.find((n) => n.trim().toLowerCase() === SHEET_NAME);
  if (employeeDataSheet) return employeeDataSheet;

  const [firstSheet] = sheetNames;
  if (firstSheet && !NON_DATA_SHEET_NAMES.includes(firstSheet.trim().toLowerCase())) {
    return firstSheet;
  }

  return sheetNames.find((n) => !NON_DATA_SHEET_NAMES.includes(n.trim().toLowerCase()));
};

const parseFileHeaders = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        if (!workbook.SheetNames.length) return reject('The file has no sheets.');
        const sheetName = selectEmployeeDataSheet(workbook.SheetNames);
        if (!sheetName)
          return reject('Could not find an "Employee Data" sheet. Please use the downloaded template or a single-sheet file.');
        const rawRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
        if (rawRows.length === 0)
          return reject('The sheet has no data rows. Please add at least one employee.');
        if (rawRows.length > MAX_ROWS)
          return reject(`File exceeds the ${MAX_ROWS.toLocaleString()} row limit (found ${rawRows.length.toLocaleString()}).`);
        resolve(Object.keys(rawRows[0]));
      } catch {
        reject('Failed to read the file. Please ensure it is a valid .xls or .xlsx file.');
      }
    };
    reader.onerror = () => reject('Failed to read the file.');
    reader.readAsArrayBuffer(file);
  });

const OrganizationHierarchy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email: contextEmail } = useContext(AuthContext);
  const email = contextEmail || localStorage.getItem('username') || '';

  const [fileHeaders,  setFileHeaders] = useState([]);
  const [columnMap,    setColumnMap]   = useState({});
  const [nameMode,     setNameMode]    = useState({ emp: 'split', mgr: 'split', hrbp: 'split' });
  const [fullNameCol,  setFullNameCol] = useState({ emp: null,    mgr: null,    hrbp: null    });

  const [file,        setFile]        = useState(null);
  const [uploadResult, setUploadResult] = useState(null); // { upload_id, total_rows, status }
  const [mapping,     setMapping]     = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Priority: SSE in-flight → notification URL redirect → fresh upload
  const [step,        setStep]        = useState(() => {
    const { isActive, uploadId: pid } = useHrisUploadStore.getState();
    if (isActive && pid) return 'processing';
    if (new URLSearchParams(window.location.search).get('upload_id')) return 'loading_url';
    return 'upload';
  });
  const [uploadId,    setUploadId]    = useState(() => {
    const { uploadId: pid } = useHrisUploadStore.getState();
    return pid || null;
  });
  const [mapped,      setMapped]      = useState([]);
  const [needsAction, setNeedsAction] = useState([]);
  const [empTab,           setEmpTab]           = useState('onboard');
  const [confirming,       setConfirming]       = useState(false);
  const [onboarding,       setOnboarding]       = useState(false);
  const [mappingConfirmed, setMappingConfirmed] = useState(false);
  const [onboardingResult, setOnboardingResult] = useState(null);
  const [onboardingResultOpen, setOnboardingResultOpen] = useState(false);
  const [onboardStatus,   setOnboardStatus]   = useState('idle'); // idle | loading | streaming | success | partial | error
  const [onboardProgress, setOnboardProgress] = useState(0);
  const [onboardTotal,    setOnboardTotal]     = useState(0);
  const [onboardMsg,      setOnboardMsg]       = useState('');
  const [onboardCounts,   setOnboardCounts]   = useState({ onboarded: 0, skipped: 0, failed: 0, total: 0 });
  const [activeTab,        setActiveTab]        = useState('mapped');
  const [resolvedMap,      setResolvedMap]      = useState({});
  const [mappedPage,       setMappedPage]       = useState(1);
  const [needsActionPage,  setNeedsActionPage]  = useState(1);
  const [tablePageSize,    setTablePageSize]    = useState(20);

  const [needsActionFilter, setNeedsActionFilter] = useState('all');

  // ── SSE progress tracking — pure SSE, no polling ──
  const sseState = useHrisUploadSSE();

  // ── Onboarding SSE callbacks (stable refs via useCallback) ──
  const handleOnboardProgress = useCallback((data) => {
    const total = Number(data.total_rows) || 0;
    setOnboardTotal(total);

    if (data.event === 'hris_onboarding_started') {
      // Job started — show total so the "0 / N" display appears immediately
      setOnboardProgress(0);
    } else {
      // hris_onboarding_progress: use onboarded_count (rows successfully onboarded so far)
      setOnboardProgress(Number(data.onboarded_count) || 0);
    }
  }, []);

  const handleOnboardTerminal = useCallback((data) => {
    const onboarded = Number(data.onboarded_count) || 0;
    const skipped   = Number(data.skipped_count)   || 0;
    const failed    = Number(data.failed_count)    || 0;
    // total_rows may not be present on hris_onboarding_failed — fall back to sum
    const total     = Number(data.total_rows) || (onboarded + skipped + failed);
    setOnboardCounts({ onboarded, skipped, failed, total });
    setOnboardMsg(data.message || '');

    if (data.status === 'Task Completed' || data.status === 'Task Completed With Warnings') {
      const isCleanSuccess = skipped === 0 && failed === 0;
      setOnboardStatus(isCleanSuccess ? 'success' : 'partial');
    } else {
      setOnboardStatus('error');
    }
  }, []);

  const handleOnboardRetryExhausted = useCallback(() => {
    setOnboardStatus('error');
    setOnboardMsg('Unable to connect to the progress stream. Please refresh the page.');
  }, []);

  useHrisOnboardSSE({
    uploadId,
    isActive: onboardStatus === 'streaming',
    onProgress: handleOnboardProgress,
    onTerminal: handleOnboardTerminal,
    onRetryExhausted: handleOnboardRetryExhausted,
  });

  // Clean success → skip the banner and navigate straight to View Employees
  // Partial / error → stay on current tab but refresh the employees table so new rows appear
  const prevOnboardStatusRef = useRef('idle');
  useEffect(() => {
    const prev = prevOnboardStatusRef.current;
    prevOnboardStatusRef.current = onboardStatus;
    if (prev !== 'streaming') return;

    if (onboardStatus === 'success') {
      resetMappingSessionAfterOnboarding(); // includes fetchOrgEmployees()
    } else if (onboardStatus === 'partial' || onboardStatus === 'error') {
      fetchOrgEmployees(); // refresh table in background so newly-onboarded rows show up
    }
  }, [onboardStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // When SSE reaches terminal → load results and go to mapping step
  const BQ_DONE = new Set(['COMPLETE', 'NEEDS_ACTION', 'FAILED']);
  useEffect(() => {
    if (!sseState?.status || !BQ_DONE.has(sseState.status) || step === 'mapping') return;
    if (sseState.status === 'FAILED') {
      SbNotification.error('Mapping failed', sseState.message || 'Mapping job failed.');
      setStep('upload');
      useHrisUploadStore.getState().clearUpload();
      return;
    }
    // Terminal success — load results
    (async () => {
      try {
        const result = await processOrgHierarchyMapping(uploadId);
        const allMappings = result?.mappings ?? [];
        const needsActionRows = allMappings.filter((m) => m.status === 'NEEDS_ACTION' || m.status === 'FLAGGED');
        setMapped(allMappings.filter((m) => m.status === 'MAPPED'));
        setNeedsAction(needsActionRows);
        setUploadResult((prev) => ({
          ...prev,
          processed_count: result?.processed_count ?? result?.total_rows,
          total_rows: result?.total_rows ?? prev?.total_rows,
          mapped_count: result?.mapped_count,
          needs_action_count: result?.needs_action_count,
          flagged_count: result?.flagged_count,
          message: result?.message,
        }));
        const autoSkipped = needsActionRows
          .filter((m) => !m.possible_requisitions_json?.length)
          .reduce((acc, m) => ({ ...acc, [m.mapping_id]: '__skip__' }), {});
        setResolvedMap(autoSkipped);
        setMappingConfirmed(false);
        setStep('mapping');
        useHrisUploadStore.getState().clearUpload();
      } catch (err) {
        SbNotification.error('Load failed', 'Unable to load mapping results.');
        setStep('upload');
        useHrisUploadStore.getState().clearUpload();
      }
    })();
  }, [sseState?.status]);

  // Restore mapping results when arriving via bell notification (/admin-setup/employee-data?upload_id=X).
  // Skipped if SSE is already tracking this upload (isActive=true) — the SSE effect handles that path.
  useEffect(() => {
    const urlUploadId = new URLSearchParams(location.search).get('upload_id');
    if (!urlUploadId) return;
    if (useHrisUploadStore.getState().isActive) return;

    (async () => {
      try {
        const result = await processOrgHierarchyMapping(urlUploadId);
        const allMappings = result?.mappings ?? [];
        const needsActionRows = allMappings.filter((m) => m.status === 'NEEDS_ACTION' || m.status === 'FLAGGED');
        setMapped(allMappings.filter((m) => m.status === 'MAPPED'));
        setNeedsAction(needsActionRows);
        setUploadId(urlUploadId);
        setUploadResult({
          upload_id: urlUploadId,
          processed_count: result?.processed_count ?? result?.total_rows,
          total_rows: result?.total_rows,
          mapped_count: result?.mapped_count,
          needs_action_count: result?.needs_action_count,
          flagged_count: result?.flagged_count,
          message: result?.message,
        });
        const autoSkipped = needsActionRows
          .filter((m) => !m.possible_requisitions_json?.length)
          .reduce((acc, m) => ({ ...acc, [m.mapping_id]: '__skip__' }), {});
        setResolvedMap(autoSkipped);
        setMappingConfirmed(false);
        setStep('mapping');
        useNotificationStoreV2.getState().removeById(`hris-mapping-${urlUploadId}`);
      } catch (_) {
        SbNotification.error('Unable to load mapping', 'Mapping results for this upload could not be retrieved.');
        setStep('upload');
      } finally {
        // Strip ?upload_id from URL so refresh / back doesn't re-trigger this restore
        navigate('/admin-setup/employee-data', { replace: true });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [orgEmpData,    setOrgEmpData]    = useState([]);
  const [orgEmpLoading, setOrgEmpLoading] = useState(false);
  const [orgEmpError,   setOrgEmpError]   = useState(false);
  const [orgEmpFetched, setOrgEmpFetched] = useState(false);
  const [orgEmpSearch, setOrgEmpSearch] = useState('');

  const orgEmpSearchOptions = useMemo(() => {
    const vals = new Set();
    orgEmpData.forEach(r => {
      const name = `${r.first_name || ''} ${r.last_name || ''}`.trim();
      if (name) vals.add(name);
      if (r.emp_id) vals.add(String(r.emp_id));
    });
    return Array.from(vals).sort((a, b) => {
      const aNum = /^\d/.test(a), bNum = /^\d/.test(b);
      if (aNum && !bNum) return -1;
      if (!aNum && bNum) return 1;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    }).map(v => ({ value: v, label: v }));
  }, [orgEmpData]);

  const searchFilteredData = useMemo(() => {
    if (!orgEmpSearch) return orgEmpData;
    const term = orgEmpSearch.toLowerCase();
    return orgEmpData.filter(r => {
      const name = `${r.first_name || ''} ${r.last_name || ''}`.trim().toLowerCase();
      const empId = String(r.emp_id || '').toLowerCase();
      return name.includes(term) || empId.includes(term);
    });
  }, [orgEmpData, orgEmpSearch]);

  const empFunctionFilters    = useMemo(() => [...new Set(searchFilteredData.map(r => r.resolved_function || r.hris_function).filter(Boolean))].sort().map(v => ({ text: v, value: v })), [searchFilteredData]);
  const empJobCodeFilters     = useMemo(() => [...new Set(searchFilteredData.map(r => r.hris_job_code).filter(Boolean))].sort().map(v => ({ text: v, value: v })), [searchFilteredData]);
  const empJobTitleFilters    = useMemo(() => [...new Set(searchFilteredData.map(r => r.matched_role_title || r.hris_job_title).filter(Boolean))].sort().map(v => ({ text: v, value: v })), [searchFilteredData]);
  const empLegalEntityFilters = useMemo(() => [...new Set(searchFilteredData.map(r => r.resolved_legal_entity || r.hris_legal_entity).filter(Boolean))].sort().map(v => ({ text: v, value: v })), [searchFilteredData]);
  const empBizUnitFilters     = useMemo(() => [...new Set(searchFilteredData.map(r => r.hris_business_unit).filter(Boolean))].sort().map(v => ({ text: v, value: v })), [searchFilteredData]);

  const orgEmpCols = useMemo(() => [
    { title: 'Employee ID',         key: 'emp_id',            width: 120, render: (_, r) => r.emp_id || '-' },
    { title: 'Name',                key: 'name',              width: 140, render: (_, r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || '-' },
    { title: 'Job Code',            key: 'hris_job_code',     width: 100, filters: empJobCodeFilters,     filterSearch: true, onFilter: (v, r) => r.hris_job_code === v,                                                render: (_, r) => r.hris_job_code || '-' },
    { title: 'Job Title',           key: 'hris_job_title',    width: 160, filters: empJobTitleFilters,    filterSearch: true, onFilter: (v, r) => (r.matched_role_title || r.hris_job_title) === v,                     render: (_, r) => r.matched_role_title || r.hris_job_title || '-' },
    { title: 'Function',            key: 'function',          width: 130, filters: empFunctionFilters,    filterSearch: true, onFilter: (v, r) => (r.resolved_function || r.hris_function) === v,                       render: (_, r) => r.resolved_function || r.hris_function || '-' },
    { title: 'Legal Entity',        key: 'legal_entity',      width: 140, filters: empLegalEntityFilters, filterSearch: true, onFilter: (v, r) => (r.resolved_legal_entity || r.hris_legal_entity) === v,               render: (_, r) => r.resolved_legal_entity || r.hris_legal_entity || '-' },
    { title: 'Business Unit',       key: 'business_unit',     width: 130, filters: empBizUnitFilters,     filterSearch: true, onFilter: (v, r) => r.hris_business_unit === v,                                           render: (_, r) => r.hris_business_unit || '-' },
    { title: 'Manager Employee ID', key: 'manager_emp_id',    width: 155, render: (_, r) => r.manager_emp_id || '-' },
    { title: 'Manager Name',        key: 'mgr_name',          width: 140, render: (_, r) => `${r.manager_first_name || ''} ${r.manager_last_name || ''}`.trim() || '-' },
    { title: 'HRBP Employee ID',    key: 'hrbp_emp_id',       width: 145, render: (_, r) => r.hrbp_emp_id || '-' },
    { title: 'HRBP Name',           key: 'hrbp_name',         width: 130, render: (_, r) => `${r.hrbp_first_name || ''} ${r.hrbp_last_name || ''}`.trim() || '-' },
    { title: 'Email',               key: 'employee_email_id', width: 200, ellipsis: true, render: (_, r) => r.employee_email_id || '-' },
    { title: 'Date of Joining',     key: 'date_of_joining',   width: 130, render: (_, r) => formatOnboardingDate(r.hris_date_of_joining) },
    { title: 'Role Start Date',     key: 'role_start_date',   width: 130, render: (_, r) => formatOnboardingDate(r.hris_role_start_date) },
  ], [empFunctionFilters, empJobCodeFilters, empJobTitleFilters, empLegalEntityFilters, empBizUnitFilters]);

  // Selection updates local state only — API is called on Confirm Mapping so the
  // final selection per row is what gets sent, even if the user changed their mind.
  const handleResolve = (mappingId, value) => {
    setResolvedMap((prev) => ({ ...prev, [mappingId]: value }));
    setMappingConfirmed(false);
  };

  const actionableRows = (needsAction ?? []).filter((item) => item.possible_requisitions_json?.length > 0);
  const resolvedCount = actionableRows.filter((item) => resolvedMap[item.mapping_id]).length;
  const totalActionNeeded = actionableRows.length;
  const allResolved = totalActionNeeded === 0 || resolvedCount === totalActionNeeded;
  // Confirm Mapping only needs at least one resolved row — no need to force resolving every row before confirming
  const canConfirmMapping = totalActionNeeded === 0 || resolvedCount > 0;

  const actionNeededLabel = (() => {
    if (totalActionNeeded === 0) return 'Action Needed (0)';
    if (allResolved) return `Resolved ✓`;
    if (resolvedCount > 0) return `Action Needed (${resolvedCount}/${totalActionNeeded})`;
    return `Action Needed (${totalActionNeeded})`;
  })();

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await downloadOrgHierarchyTemplate();
      const url = URL.createObjectURL(response.data);
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=').pop().replace(/"/g, '').trim()
        : 'org_hierarchy_template.xlsx';
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      SbNotification.success('Template downloaded', 'Fill in the employee data, then upload.');
    } catch {
      SbNotification.error('Download failed', 'Unable to download the template. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = async (selectedFile) => {
    const formatError = await validateExcelFile(selectedFile, 'employee data upload');
    if (formatError) { SbNotification.error('Invalid file', formatError); return false; }
    try {
      const headers = await parseFileHeaders(selectedFile);
      setFileHeaders(headers);
      setFile(selectedFile);
      setUploadResult(null);
      setColumnMap({});
      setNameMode({ emp: 'split', mgr: 'split', hrbp: 'split' });
      setFullNameCol({ emp: null, mgr: null, hrbp: null });
    } catch (err) {
      SbNotification.error('Validation failed', err);
    }
    return false;
  };

  const handlePreview = () => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCancelProcessing = () => {
    showSbConfirm({
      title: 'Discard this upload?',
      content: 'The mapping job is still running. Cancelling now will discard this file — employee records cannot be saved or onboarded until you re-upload and complete the process.',
      okText: 'Discard Upload',
      cancelText: 'Keep Waiting',
      okButtonProps: { danger: true },
      onOk: () => {
        useHrisUploadStore.getState().clearUpload();
        setStep('upload');
        setUploadId(null);
        setFile(null);
        setFileHeaders([]);
        setColumnMap({});
        setNameMode({ emp: 'split', mgr: 'split', hrbp: 'split' });
        setFullNameCol({ emp: null, mgr: null, hrbp: null });
        setUploadResult(null);
      },
    });
  };

  const handleCancelOnboarding = () => {
    showSbConfirm({
      title: 'Cancel onboarding?',
      content: 'The onboarding job is still running in the background. Dismissing here ends this session — the job may still complete. You can check back via the notification bell.',
      okText: 'Dismiss Session',
      cancelText: 'Keep Waiting',
      okButtonProps: { danger: true },
      onOk: () => {
        resetMappingSessionAfterOnboarding();
      },
    });
  };

  const handleDiscard = () => {
    setFile(null);
    setUploadResult(null);
    setFileHeaders([]);
    setColumnMap({});
    setNameMode({ emp: 'split', mgr: 'split', hrbp: 'split' });
    setFullNameCol({ emp: null, mgr: null, hrbp: null });
  };

  const handleNameModeChange = (group, mode) => {
    const cfg = NAME_GROUP_CONFIG[group];
    setNameMode((prev) => ({ ...prev, [group]: mode }));
    setFullNameCol((prev) => ({ ...prev, [group]: null }));
    setColumnMap((prev) => {
      const next = { ...prev };
      delete next[cfg.fullKey];
      delete next[cfg.firstKey];
      delete next[cfg.lastKey];
      return next;
    });
  };

  const handleFullNameColChange = (group, val) => {
    const cfg = NAME_GROUP_CONFIG[group];
    setFullNameCol((prev) => ({ ...prev, [group]: val ?? null }));
    setColumnMap((prev) => {
      const next = { ...prev };
      if (val) {
        next[cfg.fullKey] = val;
      } else {
        delete next[cfg.fullKey];
      }
      delete next[cfg.firstKey];
      delete next[cfg.lastKey];
      return next;
    });
  };

  const handleBack = () => {
    if (empTab === 'view') {
      navigate('/admin-setup');
      return;
    }
    if (mapping) {
      showSbConfirm({
        title: 'Upload in progress',
        content: 'Your file is currently being uploaded and processed. Going back now will discard this upload.',
        okText: 'Go Back',
        cancelText: 'Stay',
        onOk: () => navigate('/admin-setup'),
      });
      return;
    }
    if (step === 'mapping' && (mapped.length > 0 || needsAction.length > 0)) {
      showSbConfirm({
        title: 'Discard mapping results?',
        content: 'Mapping results will be lost. You will need to re-upload the file to onboard employees.',
        okText: 'Discard & Go Back',
        cancelText: 'Stay',
        okButtonProps: { danger: true },
        onOk: () => {
          setStep('upload');
          setUploadId(null);
          setMapped([]);
          setNeedsAction([]);
          setActiveTab('mapped');
          setResolvedMap({});
          setMappingConfirmed(false);
          setMappedPage(1);
          setNeedsActionPage(1);
          setNeedsActionFilter('all');
          navigate('/admin-setup');
        },
      });
      return;
    }
    navigate('/admin-setup');
  };

  // Warn on browser tab close / page refresh while mapping results are pending
  useEffect(() => {
    const shouldWarn = step === 'mapping' && (mapped.length > 0 || needsAction.length > 0);
    if (!shouldWarn) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step, mapped.length, needsAction.length]);

  const handleEmpTabChange = (tab) => {
    if (tab === 'view' && empTab === 'onboard' && step === 'mapping' && onboardStatus === 'idle' && (!allResolved || totalActionNeeded > 0)) {
      showSbConfirm({
        title: 'Pending mapping actions',
        content: 'You have unresolved role mappings. Switching to View Employee will discard your current mapping session and take you back to the upload step.',
        okText: 'Discard & View Employees',
        cancelText: 'Stay & Complete',
        onOk: () => {
          setStep('upload');
          setMapped([]);
          setNeedsAction([]);
          setActiveTab('mapped');
          setResolvedMap({});
          setMappingConfirmed(false);
          setMappedPage(1);
          setNeedsActionPage(1);
          setNeedsActionFilter('all');
          setEmpTab('view');
        },
      });
      return;
    }
    setEmpTab(tab);
  };

  const handleMapEmployees = async () => {
    setMapping(true);
    try {
      const cleanColumnMap = Object.fromEntries(Object.entries(columnMap).filter(([, v]) => v != null));
      const { upload_id, total_rows, processed_count, mapped_count, needs_action_count, flagged_count, status, message } = await uploadOrgHierarchyEmployees(
        file,
        cleanColumnMap,
      );
      setUploadResult({ upload_id, total_rows, processed_count, mapped_count, needs_action_count, flagged_count, status, message });
      setUploadId(upload_id);

      // Activate SSE tracking — the Cloud Run Job will process async
      useHrisUploadStore.getState().setUpload(upload_id, total_rows);
      setStep('processing');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Unable to process employee data. Please try again.';
      SbNotification.error('Mapping failed', msg);
    } finally {
      setMapping(false);
    }
  };

  const fetchOrgEmployees = async () => {
    setOrgEmpLoading(true);
    setOrgEmpError(false);
    try {
      const response = await getOrgHierarchyEmployees({ limit: EMP_VIEW_FETCH_ALL_LIMIT });
      setOrgEmpData(response?.employees || []);
      setOrgEmpFetched(true);
    } catch {
      SbNotification.error('Failed to load employees', 'Unable to fetch employee data.');
      setOrgEmpError(true);
    } finally {
      setOrgEmpLoading(false);
    }
  };

  // Lazy-load employee data the first time the View Employee tab is opened.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (empTab === 'view' && !orgEmpFetched && !orgEmpLoading && !orgEmpError) {
      fetchOrgEmployees();
    }
  }, [empTab]);

  const handleConfirmMapping = () => {
    showSbConfirm({
      title: 'Confirm mapping?',
      content: 'This will save the reviewed HRIS role mappings. You can onboard mapped employees after mapping is confirmed.',
      okText: 'Confirm Mapping',
      cancelText: 'Review Again',
      onOk: async () => {
        await confirmMappingOnly();
      },
    });
  };

  const confirmMappingOnly = async () => {
    const mappings = Object.entries(resolvedMap)
      .filter(([, val]) => val && val !== '__skip__')
      .map(([mapping_id, selected_requisition]) => ({ mapping_id, selected_requisition }));
    setConfirming(true);
    try {
      await confirmOrgHierarchyMapping(mappings);
      setMappingConfirmed(true);
      SbNotification.success('Mapping confirmed', 'You can now onboard mapped employees.');
      useNotificationStoreV2.getState().removeById(`hris-mapping-${uploadId}`);
    } catch (err) {
      const httpStatus = err?.response?.status;
      const msg = httpStatus >= 500
        ? 'Something went wrong on our end. Please contact the support team.'
        : httpStatus >= 400
        ? err?.response?.data?.message || 'Unable to confirm mapping. Please review the mapping and try again.'
        : err?.response?.data?.message || 'Unable to confirm mapping. Please try again.';
      SbNotification.error('Confirm mapping failed', msg);
    } finally {
      setConfirming(false);
    }
  };

  // When every employee auto-mapped cleanly (no rows in Needs Action), there is nothing to
  // resolve — the resolve-mapping API rejects an empty resolutions array, and there's nothing
  // to persist anyway since those rows already carry their matched requisition. Skip the manual
  // Confirm Mapping step locally so only "Onboard Mapped Employees" is presented.
  const autoConfirmedUploadRef = useRef(null);
  useEffect(() => {
    if (step !== 'mapping' || onboardStatus !== 'idle') return;
    if (needsAction.length > 0) return;
    if (mappingConfirmed || !uploadId) return;
    if (autoConfirmedUploadRef.current === uploadId) return;
    autoConfirmedUploadRef.current = uploadId;
    setMappingConfirmed(true);
  }, [step, onboardStatus, needsAction.length, mappingConfirmed, uploadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnboardMappedEmployees = () => {
    showSbConfirm({
      title: 'Onboard mapped employees?',
      content: 'This will onboard all mapped employees from this HRIS upload. SkillsBridge will resolve employee users, job descriptions, and reporting manager IDs on the backend.',
      okText: 'Onboard Employees',
      cancelText: 'Review Again',
      onOk: async () => {
        await onboardMappedEmployees();
      },
    });
  };

  const onboardMappedEmployees = async () => {
    if (!mappingConfirmed) {
      SbNotification.warning('Confirm mapping first', 'Please confirm the reviewed role mappings before onboarding employees.');
      return;
    }

    setOnboarding(true);
    setOnboardStatus('loading');
    setOnboardProgress(0);
    setOnboardTotal(0);
    setOnboardMsg('');
    try {
      const result = await onboardMappedOrgHierarchyEmployees({ uploadId });
      setOnboarding(false);
      if (result?.dispatched) {
        setOnboardStatus('streaming');
      } else {
        setOnboardStatus('idle');
        SbNotification.error('Onboarding failed', result?.message || 'Unexpected response from server.');
      }
    } catch (err) {
      setOnboarding(false);
      const httpStatus = err?.response?.status;
      const msg = httpStatus >= 500
        ? 'Something went wrong on our end. Please contact the support team.'
        : err?.response?.data?.message || 'Unable to onboard employees. Please try again.';
      setOnboardStatus('error');
      setOnboardMsg(msg);
    }
  };

  const handleRetryOnboard = () => {
    setOnboardStatus('idle');
    setOnboardProgress(0);
    setOnboardTotal(0);
    setOnboardMsg('');
  };

  const getOnboardingResultCounts = (result) => {
    const rows = Array.isArray(result?.results) ? result.results : [];
    return {
      totalMapped: Number(result?.total_mapped ?? result?.total_requested ?? result?.total_count ?? result?.total ?? rows.length ?? 0),
      onboardedCount: Number(result?.onboarded_count ?? result?.onboarded ?? result?.success_count ?? 0),
      readyCount: Number(result?.ready_count ?? 0),
      skippedCount: Number(result?.skipped_count ?? result?.skipped ?? 0),
      failedCount: Number(result?.failed_count ?? result?.failed ?? 0),
      hasWarnings: rows.some((row) => Array.isArray(row?.warnings) && row.warnings.length > 0),
    };
  };

  const buildOnboardingSummary = ({ onboardedCount, readyCount, skippedCount, failedCount }) =>
    `${onboardedCount} employees onboarded${readyCount ? `, ${readyCount} ready` : ''}, ${skippedCount} skipped, ${failedCount} failed`;

  const resetMappingSessionAfterOnboarding = () => {
    useNotificationStoreV2.getState().removeById(`hris-mapping-${uploadId}`);
    setStep('upload');
    setUploadId(null);
    setFile(null);
    setFileHeaders([]);
    setColumnMap({});
    setNameMode({ emp: 'split', mgr: 'split', hrbp: 'split' });
    setFullNameCol({ emp: null, mgr: null, hrbp: null });
    setUploadResult(null);
    setMapped([]);
    setNeedsAction([]);
    setResolvedMap({});
    setMappingConfirmed(false);
    setActiveTab('mapped');
    setMappedPage(1);
    setNeedsActionPage(1);
    setNeedsActionFilter('all');
    setOnboardStatus('idle');
    setOnboardProgress(0);
    setOnboardTotal(0);
    setOnboardMsg('');
    setOnboardCounts({ onboarded: 0, skipped: 0, failed: 0, total: 0 });
    setEmpTab('view');
    fetchOrgEmployees();
  };

  const handleCloseOnboardingResult = () => {
    const counts = getOnboardingResultCounts(onboardingResult);
    setOnboardingResultOpen(false);
    if (counts.onboardedCount > 0) {
      resetMappingSessionAfterOnboarding();
    }
  };

  const allKeyFieldsMapped = !!file && KEY_FIELDS.every((key) => !!columnMap[key]);

  const mappedCols      = buildColumns(mapped,      'mapped');
  const filteredNeedsAction = needsActionFilter === 'multiple'
    ? needsAction.filter((r) => (r.possible_requisitions_json?.length ?? 0) > 0)
    : needsActionFilter === 'none'
    ? needsAction.filter((r) => !r.possible_requisitions_json?.length)
    : needsAction;

  const flaggedCount     = needsAction.filter((m) => m.status === 'FLAGGED').length;
  const needsActionCount = needsAction.filter((m) => m.status === 'NEEDS_ACTION').length;

  const needsActionCols = buildColumns(needsAction, 'needs_action', resolvedMap, handleResolve, needsActionFilter, setNeedsActionFilter, mappingConfirmed);

  const activeCols = activeTab === 'mapped' ? mappedCols : needsActionCols;
  const tableScrollX = activeCols.reduce((sum, col) => sum + (col.width || 0), 0);

  const onboardingResultCounts = getOnboardingResultCounts(onboardingResult);
  const onboardingResultRows = Array.isArray(onboardingResult?.results) ? onboardingResult.results : [];
  const onboardingResultColumns = [
    { title: 'Emp ID', dataIndex: 'emp_id', key: 'emp_id', width: 90, render: (val) => val || '-' },
    { title: 'Email', dataIndex: 'employee_email_id', key: 'employee_email_id', width: 190, ellipsis: true, render: (val) => val || '-' },
    { title: 'Requisition', dataIndex: 'matched_sb_requisition_number', key: 'matched_sb_requisition_number', width: 150, render: (val) => val || '-' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const normalized = String(status || '').toUpperCase();
        const color = normalized === 'ONBOARDED' ? 'success' : normalized === 'READY' ? 'processing' : normalized === 'SKIPPED' ? 'warning' : normalized === 'FAILED' ? 'error' : 'default';
        return <SbTag color={color}>{normalized || '-'}</SbTag>;
      },
    },
    { title: 'Message / Reason', key: 'message', width: 260, render: (_, row) => row?.message || row?.reason || '-' },
    {
      title: 'Warnings',
      key: 'warnings',
      width: 260,
      render: (_, row) => Array.isArray(row?.warnings) && row.warnings.length > 0 ? row.warnings.join('; ') : '-',
    },
  ];

  return (
    <>
    {(confirming || mapping || onboarding) && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <SbSpin spinning />
      </div>
    )}
    <SbModal
      open={onboardingResultOpen}
      title="Onboarding Results"
      width={960}
      onCancel={handleCloseOnboardingResult}
      footer={[
        <SbButton key="ok" buttonType="primary" onClick={handleCloseOnboardingResult}>
          {onboardingResultCounts.onboardedCount > 0 ? 'View Employees' : 'Close'}
        </SbButton>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ color: '#595959' }}>
          {onboardingResult?.message || buildOnboardingSummary(onboardingResultCounts)}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <SbTag>Total mapped: {onboardingResultCounts.totalMapped}</SbTag>
          <SbTag color="success">Onboarded: {onboardingResultCounts.onboardedCount}</SbTag>
          {onboardingResultCounts.readyCount > 0 && <SbTag color="processing">Ready: {onboardingResultCounts.readyCount}</SbTag>}
          <SbTag color={onboardingResultCounts.skippedCount > 0 ? 'warning' : 'default'}>Skipped: {onboardingResultCounts.skippedCount}</SbTag>
          <SbTag color={onboardingResultCounts.failedCount > 0 ? 'error' : 'default'}>Failed: {onboardingResultCounts.failedCount}</SbTag>
          {onboardingResultCounts.hasWarnings && <SbTag color="warning">Warnings present</SbTag>}
        </div>
        <SbTable
          columns={onboardingResultColumns}
          dataSource={onboardingResultRows}
          rowKey={(row) => row?.mapping_id || row?.emp_id || row?.employee_email_id}
          pagination={false}
          size="small"
          scroll={{ x: 1070, y: 320 }}
        />
      </div>
    </SbModal>
    <PageContainer>
      <HeaderSection>
        <HeaderTopRow>
          <HeaderLeft>
            <BackButton onClick={handleBack} aria-label="Back">
              <ArrowLeftOutlined />
            </BackButton>
            <TitleGroup>
              <PageTitle>Employee Data</PageTitle>
              <PageSubtitle>
                Upload Employee Data from HRIS
                <SubtitleArrow>→</SubtitleArrow>
                Map employee to roles
                <SubtitleArrow>→</SubtitleArrow>
                Build Role to Role Reporting Hierarchy
              </PageSubtitle>
            </TitleGroup>
          </HeaderLeft>

          <BulkToolbarRow>
            {empTab === 'onboard' && step === 'upload' && (
              <PremiumButtonWrapper style={{ marginLeft: 'auto' }}>
                <SbButton
                  buttonType="secondary"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadTemplate}
                  loading={downloading}
                  disabled={!!file}
                >
                  Download Template
                </SbButton>
              </PremiumButtonWrapper>
            )}
            {empTab === 'onboard' && step === 'mapping' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#595959', marginRight: 8 }}>
                  {(uploadResult?.processed_count ?? uploadResult?.total_rows) != null && (
                    <span>Total: <strong>{(uploadResult.processed_count ?? uploadResult.total_rows).toLocaleString()}</strong></span>
                  )}
                  {activeTab === 'needs_action' && (
                    <span>Flagged: <strong>{flaggedCount}</strong></span>
                  )}
                </div>
                <MappingTabBar>
                  <MappingTabBtn $active={activeTab === 'mapped'} onClick={() => setActiveTab('mapped')}>
                    Auto Mapped ({mapped?.length ?? 0})
                  </MappingTabBtn>
                  <MappingTabBtn $active={activeTab === 'needs_action'} $resolved={allResolved && totalActionNeeded > 0} onClick={() => setActiveTab('needs_action')}>
                    {actionNeededLabel}
                  </MappingTabBtn>
                </MappingTabBar>
              </>
            )}
            {empTab === 'view' && (
              <EmpSearchWrapperStyled style={{ marginLeft: 'auto' }}>
                <SbSelect
                  options={orgEmpSearchOptions}
                  value={orgEmpSearch || undefined}
                  suffixIcon={<SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />}
                  onChange={(val) => setOrgEmpSearch(val || '')}
                  placeholder="Search by name or Employee ID…"
                  allowClear
                  showSearch
                  filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                  notFoundContent="No matching results"
                  listHeight={300}
                />
              </EmpSearchWrapperStyled>
            )}
          </BulkToolbarRow>
        </HeaderTopRow>

        <TabsRow>
          <SbTabs
            activeKey={empTab}
            onChange={handleEmpTabChange}
            items={EMP_TABS}
          />
          {empTab === 'onboard' && step === 'upload' && file && (
            <SbFilePill fileName={file.name} onPreview={mapping ? undefined : handlePreview} onRemove={mapping ? undefined : handleDiscard} />
          )}
        </TabsRow>

        {/* Journey bar — part of header, same row pattern as BulkJdUpload StepBarStyled */}
        {empTab === 'onboard' && (() => {
          const activeStep = step === 'processing' || step === 'mapping' ? 2 : file ? 1 : 0;
          return (
            <OnboardJourneyRow>
              <OnboardJourneyBar>
                {ONBOARD_JOURNEY.map((label, i) => (
                  <React.Fragment key={label}>
                    <OnboardJourneyStep>
                      <OnboardJourneyBadge $active={i === activeStep} $done={i < activeStep}>{i + 1}</OnboardJourneyBadge>
                      <OnboardJourneyLabel $active={i === activeStep} $done={i < activeStep}>{label}</OnboardJourneyLabel>
                    </OnboardJourneyStep>
                    {i < ONBOARD_JOURNEY.length - 1 && <OnboardJourneyDivider>›</OnboardJourneyDivider>}
                  </React.Fragment>
                ))}
              </OnboardJourneyBar>
            </OnboardJourneyRow>
          );
        })()}

        {empTab === 'onboard' && step === 'mapping' && (
          <>
            <MappingActionRow>
              <PremiumButtonWrapper>
                <MappingActionsGroup>
                  {mappingConfirmed ? (
                    <MappingConfirmedText>Mapping Confirmed ✓</MappingConfirmedText>
                  ) : activeTab === 'needs_action' ? (
                    <SbButton
                      buttonType="primary"
                      size="small"
                      onClick={handleConfirmMapping}
                      disabled={!canConfirmMapping || confirming || onboardStatus !== 'idle' || !uploadId}
                    >
                      Confirm Mapping
                    </SbButton>
                  ) : null}
                  {onboardStatus === 'idle' && (
                    <SbButton
                      buttonType="primary"
                      size="small"
                      onClick={handleOnboardMappedEmployees}
                      disabled={!mappingConfirmed || onboarding || confirming || !uploadId}
                    >
                      Onboard Mapped Employees ({mapped.length + resolvedCount})
                    </SbButton>
                  )}
                  {onboardStatus === 'loading' && (
                    <SbButton buttonType="primary" size="small" disabled loading>
                      Dispatching…
                    </SbButton>
                  )}
                  {onboardStatus === 'streaming' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <LoadingOutlined style={{ color: '#1677ff' }} />
                      <span style={{ fontSize: 12, color: '#595959' }}>
                        {onboardTotal > 0
                          ? `${onboardProgress} / ${onboardTotal} employees onboarded`
                          : onboardMsg || 'Onboarding in progress…'}
                      </span>
                    </div>
                  )}
                </MappingActionsGroup>
              </PremiumButtonWrapper>
            </MappingActionRow>

            {(onboardStatus === 'partial' || onboardStatus === 'error') && (
              <OnboardResultBanner $variant={onboardStatus === 'error' ? 'error' : 'warning'}>
                <OnboardResultIcon $variant={onboardStatus === 'error' ? 'error' : 'warning'}>
                  {onboardStatus === 'error' ? <CloseCircleFilled /> : <ExclamationCircleFilled />}
                </OnboardResultIcon>
                <OnboardResultBody $variant={onboardStatus === 'error' ? 'error' : 'warning'}>
                  {onboardStatus === 'partial'
                    ? (onboardMsg || `${onboardCounts.onboarded} of ${onboardCounts.total} onboarded${onboardCounts.skipped > 0 ? `. ${onboardCounts.skipped} skipped` : ''}${onboardCounts.failed > 0 ? `. ${onboardCounts.failed} failed` : ''}.`)
                    : (onboardMsg || (onboardCounts.total > 0
                        ? `0 of ${onboardCounts.total} onboarded${onboardCounts.skipped > 0 ? `. ${onboardCounts.skipped} skipped` : ''}${onboardCounts.failed > 0 ? `. ${onboardCounts.failed} failed` : ''}.`
                        : 'Onboarding failed'))}
                </OnboardResultBody>
                <OnboardResultActions>
                  {onboardStatus === 'partial' ? (
                    <SbButton size="small" onClick={resetMappingSessionAfterOnboarding}>
                      View Employees
                    </SbButton>
                  ) : (
                    <SbButton size="small" onClick={handleRetryOnboard}>Retry</SbButton>
                  )}
                </OnboardResultActions>
              </OnboardResultBanner>
            )}
          </>
        )}
      </HeaderSection>

      <ContentSection>

        {/* Step 1a — no file yet: upload dragger */}
        {empTab === 'onboard' && step === 'upload' && !file && (
          <UploadCTASection>
            <HrisDraggerWrap>
              <Upload.Dragger
                accept=".xls,.xlsx"
                maxCount={1}
                showUploadList={false}
                multiple={false}
                beforeUpload={handleFileSelect}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Upload your employee data from HRIS</p>
                <p className="ant-upload-hint">
                  Click to select or drag and drop your file here (.xls or .xlsx) · Up to 5,000 rows per upload<br />
                  Provide Emp ID, Job Code, Job Title, Function, Sub-Function, Manager Emp ID and HRBP Emp ID to get started.
                </p>
              </Upload.Dragger>
            </HrisDraggerWrap>
          </UploadCTASection>
        )}

        {/* Step 1b — file selected: show column mapping */}
        {empTab === 'onboard' && step === 'upload' && file && (
          <ColMapSection>
            <ColMapCard>
              {/* Fixed top — always visible regardless of scroll */}
              <ColMapCardFixed>
                <ColMapTableHeader>
                  <ColMapThField>SkillsBridge Fields</ColMapThField>
                  <ColMapThArrow />
                  <ColMapThFile>Columns / Headers from File</ColMapThFile>
                  <ColMapThStatus />
                </ColMapTableHeader>
              </ColMapCardFixed>

              {/* Scrollable field rows */}
              <ColMapCardBody>
                {FIELD_GROUPS.map(({ label: groupLabel, fields }, groupIdx) => (
                  <React.Fragment key={groupLabel}>
                    <ColMapGroupLabel>{groupIdx + 1}. {groupLabel}</ColMapGroupLabel>
                    {fields.map((field) => {
                      const { key, label, nameGroup } = field;
                      if (nameGroup) {
                        const cfg = NAME_GROUP_CONFIG[nameGroup];
                        const isFullMode = nameMode[nameGroup] === 'full';
                        const isFirstKey = key === cfg.firstKey;

                        // In full mode, skip the last-name row entirely
                        if (isFullMode && !isFirstKey) return null;

                        const nameFormatRow = isFirstKey ? (
                          <ColMapFullNameCheckRow key={`${nameGroup}-format`}>
                            <span>Name format:</span>
                            <SbTooltip title={cfg.hint}>
                              <Checkbox
                                checked={isFullMode}
                                disabled={mapping}
                                onChange={(e) => handleNameModeChange(nameGroup, e.target.checked ? 'full' : 'split')}
                              >
                                Full Name
                              </Checkbox>
                            </SbTooltip>
                          </ColMapFullNameCheckRow>
                        ) : null;

                        if (isFullMode) {
                          const fullCol = fullNameCol[nameGroup] ?? undefined;
                          return (
                            <ColMapFieldRow key={key}>
                              <ColMapFieldCell>
                                <ColMapFieldLabel>{cfg.fullLabel}</ColMapFieldLabel>
                              </ColMapFieldCell>
                              <ColMapArrowCol><ColMapFieldArrow /></ColMapArrowCol>
                              <ColMapSelectCell>
                                {nameFormatRow}
                                <Select
                                  allowClear size="small"
                                  disabled={mapping}
                                  placeholder="Select full name column"
                                  value={fullCol}
                                  onChange={(val) => handleFullNameColChange(nameGroup, val)}
                                  options={fileHeaders.map((h) => ({ label: h, value: h }))}
                                />
                              </ColMapSelectCell>
                              <ColMapStatusCell>
                                {fullCol ? <ColMapStatusCheckIcon /> : <ColMapStatusPendingIcon />}
                              </ColMapStatusCell>
                            </ColMapFieldRow>
                          );
                        }

                        // Split mode
                        const isMapped = !!columnMap[key];
                        return (
                          <ColMapFieldRow key={key}>
                            <ColMapFieldCell>
                              <ColMapFieldLabel>{label}</ColMapFieldLabel>
                            </ColMapFieldCell>
                            <ColMapArrowCol><ColMapFieldArrow /></ColMapArrowCol>
                            <ColMapSelectCell>
                              {nameFormatRow}
                              <Select
                                allowClear size="small"
                                disabled={mapping}
                                placeholder="Select column from file"
                                value={columnMap[key] ?? undefined}
                                onChange={(val) => setColumnMap((prev) => ({ ...prev, [key]: val ?? null }))}
                                options={fileHeaders.map((h) => ({ label: h, value: h }))}
                              />
                            </ColMapSelectCell>
                            <ColMapStatusCell>
                              {isMapped ? <ColMapStatusCheckIcon /> : <ColMapStatusPendingIcon />}
                            </ColMapStatusCell>
                          </ColMapFieldRow>
                        );
                      }

                      // Standard (non-name) field
                      const isMapped = !!columnMap[key];
                      const isKeyField = KEY_FIELDS.includes(key);
                      const isEmail = field.validate === 'email';
                      return (
                        <ColMapFieldRow key={key}>
                          <ColMapFieldCell>
                            <ColMapFieldLabel>
                              {isEmail ? (
                                <SbTooltip title="Values in this column must be valid email addresses (e.g. user@company.com)">
                                  {label}
                                </SbTooltip>
                              ) : label}
                              {isKeyField && <ColMapReqStar>*</ColMapReqStar>}
                            </ColMapFieldLabel>
                          </ColMapFieldCell>
                          <ColMapArrowCol><ColMapFieldArrow /></ColMapArrowCol>
                          <ColMapSelectCell>
                            <Select
                              allowClear size="small"
                              disabled={mapping}
                              placeholder="Select column from file"
                              value={columnMap[key] ?? undefined}
                              onChange={(val) => setColumnMap((prev) => ({ ...prev, [key]: val ?? null }))}
                              options={fileHeaders.map((h) => ({ label: h, value: h }))}
                            />
                          </ColMapSelectCell>
                          <ColMapStatusCell>
                            {isMapped ? <ColMapStatusCheckIcon /> : <ColMapStatusPendingIcon />}
                          </ColMapStatusCell>
                        </ColMapFieldRow>
                      );
                    })}
                    {groupIdx < FIELD_GROUPS.length - 1 && <ColMapGroupDivider />}
                  </React.Fragment>
                ))}
              </ColMapCardBody>

              {/* Fixed footer — always visible */}
              <ColMapFooter>
                <PremiumButtonWrapper>
                  <SbButton
                    buttonType="primary"
                    size="small"
                    onClick={handleMapEmployees}
                    disabled={!allKeyFieldsMapped || mapping}
                  >
                    Proceed to Map Employees
                  </SbButton>
                </PremiumButtonWrapper>
              </ColMapFooter>
            </ColMapCard>
          </ColMapSection>
        )}

        {empTab === 'onboard' && step === 'processing' && (
          <ColMapSection>
            <ColMapCard>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 40px', gap: 20 }}>
                <LoadingOutlined style={{ fontSize: 36, color: '#1677ff' }} spin />
                <div style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
                  Mapping employees to roles…
                </div>
                <div style={{ width: '100%', maxWidth: 400 }}>
                  <Progress
                    percent={Number(sseState?.progress) || 0}
                    status="active"
                    strokeColor={{ from: '#1677ff', to: '#52c41a' }}
                  />
                </div>
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>
                  {`File validated — this will take a few minutes to complete.`}
                </div>
                {Number(sseState?.processed_count) > 0 && (
                  <div style={{ fontSize: 12, color: '#595959' }}>
                    {sseState.processed_count} / {sseState.total_rows || uploadResult?.total_rows || '?'} processed
                    {Number(sseState.mapped_count) > 0 && ` · ${sseState.mapped_count} mapped`}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  Processing in background, please visit back after sometime.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <SbTooltip title="Mapping continues in the background. You'll receive a notification once results are ready to review." placement="top">
                    <span>
                      <SbButton buttonType="primary" size="small" onClick={() => navigate('/admin-setup')}>
                        Go to Admin Setup
                      </SbButton>
                    </span>
                  </SbTooltip>
                  <SbButton buttonType="secondary" size="small" onClick={handleCancelProcessing}>
                    Cancel
                  </SbButton>
                </div>
              </div>
            </ColMapCard>
          </ColMapSection>
        )}

        {empTab === 'onboard' && step === 'mapping' && onboardStatus === 'streaming' && (
          <ColMapSection>
            <ColMapCard>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 40px', gap: 20 }}>
                <LoadingOutlined style={{ fontSize: 36, color: '#1677ff' }} spin />
                <div style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
                  Onboarding employees…
                </div>
                <div style={{ width: '100%', maxWidth: 400 }}>
                  <Progress
                    percent={onboardTotal > 0 ? Math.round((onboardProgress / onboardTotal) * 100) : 0}
                    status="active"
                    strokeColor={{ from: '#1677ff', to: '#52c41a' }}
                  />
                </div>
                {onboardTotal > 0 && (
                  <div style={{ fontSize: 12, color: '#595959' }}>
                    {onboardProgress} / {onboardTotal} employees onboarded
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  Processing in background — this may take a few minutes.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <SbTooltip title="Onboarding continues in the background. You'll receive a notification once complete." placement="top">
                    <span>
                      <SbButton buttonType="primary" size="small" onClick={() => navigate('/admin-setup')}>
                        Go to Admin Setup
                      </SbButton>
                    </span>
                  </SbTooltip>
                  <SbButton buttonType="secondary" size="small" onClick={handleCancelOnboarding}>
                    Cancel
                  </SbButton>
                </div>
              </div>
            </ColMapCard>
          </ColMapSection>
        )}

        {empTab === 'onboard' && step === 'mapping' && onboardStatus !== 'streaming' && (
          <PageTableWrapperStyled style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <SbTable
                columns={activeCols}
                dataSource={activeTab === 'mapped' ? mapped : filteredNeedsAction}
                rowKey="mapping_id"
                size="small"
                sticky
                pagination={false}
                scroll={{ x: tableScrollX }}
              />
            </div>
            <div style={{
              flexShrink: 0,
              background: '#fff',
              borderTop: '1px solid #f0f0f0',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              zIndex: 10,
            }}>
              <Pagination
                current={activeTab === 'mapped' ? mappedPage : needsActionPage}
                pageSize={tablePageSize}
                total={activeTab === 'mapped' ? mapped.length : filteredNeedsAction.length}
                pageSizeOptions={['20', '50', '100']}
                showSizeChanger
                showTotal={(total) => `${total} employee records`}
                onChange={(page, size) => {
                  if (activeTab === 'mapped') setMappedPage(page);
                  else setNeedsActionPage(page);
                  setTablePageSize(size);
                }}
              />
            </div>
          </PageTableWrapperStyled>
        )}

        {empTab === 'view' && (
          <PageTableWrapperStyled style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {onboardStatus === 'streaming' && (
              <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 6, margin: '0 0 12px', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 14, flexShrink: 0 }}>
                <LoadingOutlined style={{ fontSize: 22, color: '#1677ff', marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0050b3', marginBottom: 8 }}>
                    Onboarding employees…
                  </div>
                  <Progress
                    percent={onboardTotal > 0 ? Math.round((onboardProgress / onboardTotal) * 100) : 0}
                    status="active"
                    strokeColor={{ from: '#1677ff', to: '#52c41a' }}
                    size="small"
                  />
                  {onboardTotal > 0 && (
                    <div style={{ fontSize: 12, color: '#595959', marginTop: 6 }}>
                      {onboardProgress} / {onboardTotal} employees onboarded
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                    Processing in background — the table will refresh automatically when complete.
                  </div>
                </div>
              </div>
            )}
            {orgEmpError ? (
              <EmptyStateWrapper>
                <Result
                  status="info"
                  title="Employee Data View"
                  subTitle="This view will be rolled out soon. Please check back later."
                />
              </EmptyStateWrapper>
            ) : (
              <>
                <EmpViewTableWrap>
                  <SbTable
                    columns={orgEmpCols}
                    dataSource={searchFilteredData}
                    loading={orgEmpLoading}
                    rowKey="emp_id"
                    pagination={{
                      showTotal: (total) => {
                        const hasActiveFilters = total < orgEmpData.length;
                        return (
                          <div className="paic-pagination-total">
                            <FilterOutlined />
                            <span className={hasActiveFilters ? 'is-active-text' : 'is-empty-text'}>
                              {hasActiveFilters
                                ? `${total} of ${orgEmpData.length} employee records`
                                : `${total} employee records`}
                            </span>
                          </div>
                        );
                      },
                    }}
                  />
                </EmpViewTableWrap>
              </>
            )}
          </PageTableWrapperStyled>
        )}

        {empTab === 'history' && <HistoricalEmployeeDataUpload />}
      </ContentSection>
    </PageContainer>
    </>
  );
};

export default OrganizationHierarchy;
